// src/pages/ProductDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { productService } from '../services/productService';
import { reviewService } from '../services/reviewService';
import ReviewSection from '../components/Reviews/ReviewSection';

const ProductDetailPage = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, getCartItem, increaseQuantity, decreaseQuantity } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  // Pick the product object directly passed from ProductCard.jsx
  const passedProduct = location.state?.product;

  const [selectedVariant, setSelectedVariant] = useState(passedProduct || null);
  const fallbackImg = 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=500&q=80';
  const [mainImage, setMainImage] = useState(
    passedProduct?.image || 
    (passedProduct?.images && passedProduct.images[0]) || 
    passedProduct?.imageUrl || 
    fallbackImg
  );
  
  // Only load if passedProduct is missing (e.g. direct link paste)
  const [loading, setLoading] = useState(!passedProduct);
  const [reviewStats, setReviewStats] = useState({ avgRating: 0, totalCount: 0 });
  const [openSections, setOpenSections] = useState({ 'Product Information': true, 'Item Specifications': true });

  useEffect(() => {
    // Skip backend fetch if we already have the product from router state
    if (passedProduct) {
      setSelectedVariant(passedProduct);
      setLoading(false);
      return;
    }

    const fetchProductDetails = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams(location.search);
        const mode = queryParams.get('mode') || 'QUICK';

        const data = await productService.getVariantsByProductId(id, mode.toUpperCase());
        if (data && data.length > 0) {
          const vid = queryParams.get('vid');
          
          let matchedVariant = data[0];
          if (vid) {
             const found = data.find(v => String(v.outletVariantId) === String(vid) || String(v.variantId) === String(vid) || String(v.id) === String(vid));
             if (found) {
                matchedVariant = found;
             }
          }
          
          setSelectedVariant(matchedVariant); 
          setMainImage(matchedVariant.images?.[0] || matchedVariant.image || matchedVariant.imageUrl || fallbackImg);
        }
      } catch (error) {
        console.error("Failed to load product details", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [id, location.search, passedProduct]);

  useEffect(() => {
    const vId = selectedVariant?.outletVariantId || selectedVariant?.variantId || selectedVariant?.id;
    if (!vId) return;

    const fetchReviewStats = async () => {
      try {
        const data = await reviewService.getSummary(vId);
        if (data && data.reviews) {
          const avg = data.reviews.length > 0 
            ? (data.reviews.reduce((acc, r) => acc + r.rating, 0) / data.reviews.length).toFixed(1)
            : 0;
          setReviewStats({ avgRating: Number(avg), totalCount: data.totalCount || 0 });
        }
      } catch (err) {
        console.error("Failed to fetch review stats", err);
      }
    };
    fetchReviewStats();
  }, [selectedVariant]);

  const handleShare = async () => {
    const shareData = {
      title: selectedVariant.productName || selectedVariant.description || 'SV Mart Product',
      url: window.location.href,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log("Share cancelled or failed", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Product link copied to clipboard!");
    }
  };

  const toggleSection = (sectionName) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <i className="fas fa-spinner fa-spin text-4xl text-[#0F2F63] mb-4"></i>
        <p className="text-gray-500 font-bold">Loading product details...</p>
      </div>
    );
  }

  if (!selectedVariant) {
    return (
      <div className="text-center py-32 bg-white rounded-2xl mx-4 my-8 shadow-sm border border-gray-100">
        <i className="fas fa-exclamation-triangle text-6xl text-gray-300 mb-4"></i>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Product Not Found</h2>
        <p className="text-gray-500 mb-6">The item you are looking for is unavailable.</p>
        <button onClick={() => navigate(-1)} className="bg-[#0F2F63] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-sky-700">
          Go Back
        </button>
      </div>
    );
  }

  const vId = String(selectedVariant.outletVariantId || selectedVariant.variantId || selectedVariant.id || '');
  const isFavorite = isInWishlist(vId);
  const cartItem = getCartItem(selectedVariant);

  const isStandard = selectedVariant.orderType?.toUpperCase() === 'STANDARD';
  const discountPercent = selectedVariant.mrp > selectedVariant.sellingPrice 
      ? Math.round(((selectedVariant.mrp - selectedVariant.sellingPrice) / selectedVariant.mrp) * 100) 
      : 0;
            
  const displayRating = reviewStats.avgRating > 0 ? reviewStats.avgRating : (selectedVariant.rating || selectedVariant.averageRating || 0);
  const displayReviewsCount = reviewStats.totalCount > 0 ? reviewStats.totalCount : (selectedVariant.reviewsCount || selectedVariant.totalReviews || 0);
  const brandName = selectedVariant.brandName || selectedVariant.brand || '';

  // NEW: Determine stock 
  const prodStock = selectedVariant.stock !== undefined ? selectedVariant.stock : (passedProduct?.stock !== undefined ? passedProduct.stock : 1);
  const isOutOfStock = prodStock === 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-4 md:py-8 bg-white rounded-xl shadow-sm border border-gray-100 my-4">
      
      {/* Breadcrumb matching exact site look */}
      <div className="flex items-center gap-2 text-[13px] font-bold text-[#0F2F63] mb-6 px-2">
        <button onClick={() => navigate(-1)} className="hover:underline">Home</button>
        <i className="fas fa-chevron-right text-[10px] text-gray-400"></i>
        <button onClick={() => navigate(-1)} className="hover:underline">All Categories</button>
        <i className="fas fa-chevron-right text-[10px] text-gray-400"></i>
        <span className="text-gray-700 font-medium line-clamp-1">{selectedVariant.productName || selectedVariant.description}</span>
      </div>

      <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
        
        {/* LEFT - Images Gallery */}
        <div className="w-full md:w-1/2 flex gap-4 h-[400px] md:h-[500px]">
          {/* Vertical Thumbnails */}
          <div className="flex flex-col gap-3 w-16 md:w-20 overflow-y-auto hide-scrollbar">
            <style>{`.hide-scrollbar::-webkit-scrollbar { display: none !important; }`}</style>
            {selectedVariant.images?.length > 0 ? selectedVariant.images.map((img, idx) => (
              <button 
                key={idx} 
                onClick={() => setMainImage(img)}
                className={`w-full aspect-square rounded-xl border-2 overflow-hidden flex-shrink-0 transition-all p-1 ${mainImage === img ? 'border-[#0F2F63] shadow-sm' : 'border-gray-100 hover:border-gray-300'}`}
              >
                <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-contain mix-blend-multiply" />
              </button>
            )) : (
               <button className="w-full aspect-square rounded-xl border-2 border-[#0F2F63] shadow-sm overflow-hidden flex-shrink-0 p-1">
                 <img src={selectedVariant.image || selectedVariant.imageUrl || fallbackImg} alt="Thumb" className="w-full h-full object-contain mix-blend-multiply" />
               </button>
            )}
          </div>
          
          {/* Main Image View */}
          <div className="flex-1 rounded-2xl border border-gray-100 p-4 relative flex items-center justify-center shadow-sm">
            {/* FIXED: Reduced opacity if OOS */}
            <img src={mainImage} alt={selectedVariant.productName} className={`max-w-full max-h-full object-contain mix-blend-multiply ${isOutOfStock ? 'opacity-50' : ''}`} />
          </div>
        </div>

        {/* RIGHT - Details & Actions */}
        <div className="w-full md:w-1/2 flex flex-col">
          
          {/* Header Actions & Delivery Info */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1 pr-4 min-w-0">
              {brandName && <p className="text-sm font-medium text-[#0F2F63] mb-1">{brandName}</p>}
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-2 break-words" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {selectedVariant.productName || selectedVariant.description}
              </h1>
              
              {/* Dynamic Ratings Block */}
              <div className="flex items-center gap-2 mt-2">
                 <div className="flex items-center bg-gray-100 px-2 py-0.5 rounded text-sm font-bold text-gray-800">
                   {displayRating > 0 ? displayRating : 'No rating'} 
                   {displayRating > 0 && <i className="fas fa-star text-[#FF7A59] ml-1 text-xs"></i>}
                 </div>
                 {displayReviewsCount > 0 && <span className="text-sm text-gray-500">({displayReviewsCount} reviews)</span>}
              </div>
            </div>
            
            <div className="flex gap-3 flex-shrink-0">
              <button onClick={() => toggleWishlist(selectedVariant)} className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors shadow-sm">
                <i className={`fas fa-heart text-lg ${isFavorite ? 'text-red-500' : ''}`}></i>
              </button>
              <button onClick={handleShare} className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors shadow-sm">
                <i className="fas fa-share-alt text-lg"></i>
              </button>
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-4 mt-2">
             <div className="flex items-center justify-between">
                 <p className="text-sm font-medium text-gray-500 mb-1">{selectedVariant.variantName}</p>
                 <span className={`flex items-center gap-1 text-xs font-bold uppercase tracking-wider ${isStandard ? 'text-amber-500' : 'text-[#FF7A59]'}`}>
                    <i className={`fas ${isStandard ? 'fa-truck' : 'fa-bolt'}`}></i>
                    {isStandard ? 'Standard Delivery' : 'Quick Delivery'}
                 </span>
             </div>
             
             <div className="flex items-center gap-3">
                <span className="text-3xl font-black text-gray-900">₹ {selectedVariant.sellingPrice || selectedVariant.price}</span>
                {selectedVariant.mrp > (selectedVariant.sellingPrice || selectedVariant.price) && (
                  <>
                    <span className="text-lg font-medium text-gray-400 line-through">₹ {selectedVariant.mrp}</span>
                    <span className="text-sm font-bold text-[#2CCE75]">{discountPercent}% OFF</span>
                  </>
                )}
             </div>

             {/* NEW: OOS Badge Display */}
             {isOutOfStock && (
               <div className="inline-block bg-red-50 px-2.5 py-1.5 rounded-lg mt-3 border border-red-200">
                 <span className="text-red-600 font-bold text-xs uppercase tracking-wider">Out of Stock</span>
               </div>
             )}
          </div>

          {/* FIXED: Add to Cart with controls or OOS state */}
          {isOutOfStock ? (
            <div className="w-full py-3.5 bg-gray-200 text-gray-400 rounded-full font-black text-lg text-center uppercase tracking-wider mb-6 mt-4 shadow-sm">
              Out of Stock
            </div>
          ) : cartItem ? (
            <div className="flex items-center justify-between w-full md:w-1/2 py-2 bg-white border-2 border-[#0F2F63] rounded-full overflow-hidden shadow-md mb-6 mt-4 h-[52px]">
                <button onClick={() => decreaseQuantity(cartItem)} className="w-1/3 text-[#0F2F63] text-2xl font-black hover:bg-sky-50 h-full flex items-center justify-center">-</button>
                <span className="w-1/3 text-center text-lg font-bold text-gray-900">{cartItem.quantity}</span>
                <button onClick={() => increaseQuantity(cartItem)} className="w-1/3 text-[#0F2F63] text-2xl font-black hover:bg-sky-50 h-full flex items-center justify-center">+</button>
            </div>
          ) : (
            <button 
              onClick={() => { addToCart(selectedVariant); }}
              className="w-full py-3.5 bg-[#0F2F63] hover:bg-[#1a4a8a] text-white rounded-full font-bold text-lg transition-colors shadow-md mb-6 mt-4"
            >
              Add to Cart
            </button>
          )}

          {/* Accordion Specifications */}
          <div className="mt-2 border-t border-gray-100">
            {selectedVariant.groupedSpecifications ? Object.entries(selectedVariant.groupedSpecifications).map(([groupName, specs]) => (
              <div key={groupName} className="border-b border-gray-100">
                <button 
                  onClick={() => toggleSection(groupName)}
                  className="w-full py-4 flex items-center justify-between focus:outline-none"
                >
                  <span className="font-bold text-gray-800 text-[15px]">{groupName}</span>
                  <i className={`fas fa-chevron-${openSections[groupName] ? 'up' : 'down'} text-[#0F2F63] text-sm transition-transform`}></i>
                </button>
                
                {openSections[groupName] && (
                  <div className="pb-4">
                    <table className="w-full text-sm text-left">
                      <tbody>
                        {Object.entries(specs).map(([key, value], idx) => (
                          <tr key={idx} className="border-b border-gray-50 last:border-0">
                            <td className="py-2.5 pr-4 text-gray-500 w-[40%]">{key}</td>
                            <td className="py-2.5 text-gray-800 font-medium">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )) : (
              <div className="border-b border-gray-100">
                <button 
                  onClick={() => toggleSection('Details')}
                  className="w-full py-4 flex items-center justify-between focus:outline-none"
                >
                  <span className="font-bold text-gray-800 text-[15px]">Product Details</span>
                  <i className={`fas fa-chevron-${openSections['Details'] ? 'up' : 'down'} text-[#0F2F63] text-sm transition-transform`}></i>
                </button>
                {openSections['Details'] && (
                   <div className="pb-4 text-sm text-gray-600">
                     {selectedVariant.longDescription || selectedVariant.description || 'Detailed specifications are currently unavailable for this product.'}
                   </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="mt-12 border-t border-gray-100 pt-8">
        <ReviewSection outletVariantId={vId} />
      </div>

    </div>
  );
};

export default ProductDetailPage;